import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw, Volume2, VolumeX, ChevronRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ESPRESSO = '#23211E';
const GOLD = '#D89232';
const GOLD_INK = '#8F650F';
const CHAMPAGNE = '#E7CFA2';
const CREAM = '#F5F0EA';
const TERRA = '#E07A5F';

// "Die Lesung" — Audio-Artikel-Player im Magazin-Stil
const DickiesVideoLesson = ({
  lessons = [],
  title = "Lesung",
  authHeader = {},
  onComplete
}) => {
  const [currentLesson, setCurrentLesson] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [showInteraction, setShowInteraction] = useState(false);
  const [lessonStarted, setLessonStarted] = useState(false);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const lesson = lessons[currentLesson] || {};

  // Parse lesson script into sentence segments
  const parseContent = useCallback((script) => {
    if (!script) return [];
    const sentences = script.split(/(?<=[.!?])\s+/);
    return sentences.map((sentence) => ({ text: sentence.trim() })).filter(s => s.text);
  }, []);

  const segments = parseContent(lesson.script);

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Reset when lesson changes
  useEffect(() => {
    setCurrentSegment(0);
    setProgress(0);
    setIsPlaying(false);
    setLessonStarted(false);
    setShowInteraction(false);
  }, [currentLesson]);

  const startLesson = async () => {
    if (!lesson.script) return;

    setIsLoading(true);
    setLessonStarted(true);

    try {
      const response = await axios.post(
        `${API}/tts/base64`,
        { text: lesson.script, voice: "nova" },
        authHeader
      );

      const audioSrc = `data:audio/mpeg;base64,${response.data.audio}`;

      if (audioRef.current) {
        audioRef.current.src = audioSrc;
        audioRef.current.muted = isMuted;
        await audioRef.current.play();
        setIsPlaying(true);
        setIsLoading(false);

        let segmentIndex = 0;
        const segmentDuration = audioRef.current.duration / segments.length;
        setCurrentSegment(0);

        timerRef.current = setInterval(() => {
          segmentIndex++;
          if (segmentIndex < segments.length) {
            setCurrentSegment(segmentIndex);
            setProgress((segmentIndex / segments.length) * 100);
          }
        }, segmentDuration * 1000);

        audioRef.current.ontimeupdate = () => {
          if (audioRef.current && audioRef.current.duration) {
            setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
          }
        };

        audioRef.current.onended = () => {
          setIsPlaying(false);
          setShowInteraction(true);
          setProgress(100);
          clearInterval(timerRef.current);
        };
      }
    } catch (error) {
      console.error("TTS error:", error);
      setIsLoading(false);
      setLessonStarted(false);
    }
  };

  const pauseLesson = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      clearInterval(timerRef.current);
    }
  };

  const resumeLesson = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);

      const segmentDuration = audioRef.current.duration / segments.length;
      let segmentIndex = currentSegment;

      timerRef.current = setInterval(() => {
        segmentIndex++;
        if (segmentIndex < segments.length) {
          setCurrentSegment(segmentIndex);
        }
      }, segmentDuration * 1000);
    }
  };

  const resetLesson = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    clearInterval(timerRef.current);
    setCurrentSegment(0);
    setProgress(0);
    setShowInteraction(false);
    setLessonStarted(false);
    setIsPlaying(false);
  };

  const nextLesson = () => {
    resetLesson();
    if (currentLesson < lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
    } else if (onComplete) {
      onComplete();
    }
  };

  const prevLesson = () => {
    resetLesson();
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
    }
  };

  if (lessons.length === 0) {
    return (
      <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: ESPRESSO }}>
        <p className="font-display italic text-[16px]" style={{ color: CHAMPAGNE }}>
          Für dieses Kapitel ist noch keine Lesung verfügbar.
        </p>
      </div>
    );
  }

  // Truncate current sentence display to ~24 words
  const displaySentence = (text) => {
    const words = (text || '').split(/\s+/);
    if (words.length <= 24) return text;
    return words.slice(0, 24).join(' ') + ' …';
  };

  return (
    <div className="bg-cream-100 rounded-2xl border border-cream-300 overflow-hidden" data-testid="lesung-player">
      {/* Header — Espresso-Zeile */}
      <div className="px-6 py-3.5 flex items-center justify-between gap-4" style={{ backgroundColor: '#1B1A18' }}>
        <span className="font-mono text-[10px] uppercase font-semibold whitespace-nowrap" style={{ letterSpacing: '0.24em', color: GOLD }}>
          {lesson.videoUrl ? 'Lernfilm' : 'Lesung'} · {currentLesson + 1} von {lessons.length}
        </span>
        <span className="font-display italic text-[15px] truncate" style={{ color: CREAM }}>
          {lesson.title}
        </span>
        <button
          onClick={() => setIsMuted(!isMuted)}
          className="p-2 rounded-lg transition-colors flex-shrink-0"
          style={{ color: 'rgba(245,240,234,0.6)' }}
          data-testid="video-mute-btn"
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Main Content Area */}
      <div className="p-6 lg:p-8">
        {lesson.videoUrl ? (
          /* Echter Lernfilm */
          <div data-testid="lesson-video-wrap">
            <video
              key={lesson.videoUrl}
              controls
              playsInline
              preload="metadata"
              className="w-full rounded-2xl"
              style={{ backgroundColor: ESPRESSO, maxHeight: 520 }}
              src={lesson.videoUrl}
              data-testid="lesson-video-player"
            />
            {lesson.description && (
              <p className="mt-4 font-display italic text-[16px] text-center" style={{ color: '#8A8175' }}>
                {lesson.description}
              </p>
            )}
          </div>
        ) : !lessonStarted ? (
          /* Welcome Screen — Espresso-Bühne */
          <div className="rounded-2xl px-8 py-14 text-center dh-bg-dark" style={{ backgroundColor: ESPRESSO }}>
            <p className="font-display italic text-2xl md:text-[28px] max-w-xl mx-auto" style={{ color: CREAM, lineHeight: 1.35 }}>
              Hör zu — oder lies einfach mit.
            </p>
            <div className="mx-auto mt-6" style={{ width: 60, height: 1, backgroundColor: GOLD }} />
            <p className="mt-5 text-sm max-w-md mx-auto leading-relaxed" style={{ color: 'rgba(245,240,234,0.6)' }}>
              Die Dozentin liest dir die wichtigsten Konzepte dieses Kapitels vor. Die Mitschrift läuft rechts mit.
            </p>
            <button
              onClick={startLesson}
              disabled={isLoading}
              className="mt-9 mx-auto inline-flex items-center justify-center gap-2 h-[48px] px-10 rounded-full bg-transparent text-[13px] font-bold uppercase transition-colors disabled:opacity-50"
              style={{ border: `1.5px solid ${GOLD}`, color: GOLD, letterSpacing: '0.2em' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(216,146,50,0.14)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              data-testid="start-lesson-btn"
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {isLoading ? 'Lesung wird vorbereitet…' : 'Lesung beginnen'}
            </button>
          </div>
        ) : (
          /* Lesung läuft */
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

            {/* Links: Die Bühne — wanderndes Zitat */}
            <div className="lg:col-span-3 flex flex-col">
              <div className="rounded-2xl flex-1 flex flex-col justify-center px-8 py-12 min-h-[320px]" style={{ backgroundColor: ESPRESSO }}>
                {isLoading ? (
                  <p className="font-mono text-[11px] uppercase font-semibold text-center animate-pulse" style={{ letterSpacing: '0.28em', color: GOLD }}>
                    Lesung wird vorbereitet…
                  </p>
                ) : showInteraction ? (
                  <div className="text-center">
                    <p className="font-display italic text-[26px]" style={{ color: CHAMPAGNE, lineHeight: 1.4 }}>
                      Ende der Lesung.
                    </p>
                    <div className="mx-auto mt-5" style={{ width: 48, height: 1, backgroundColor: GOLD }} />
                  </div>
                ) : (
                  <div className="text-center">
                    {/* Vorheriger Satz — ausgeblendet */}
                    {currentSegment > 0 && (
                      <p className="font-display text-[15px] mb-6 transition-opacity duration-500" style={{ color: 'rgba(245,240,234,0.3)', lineHeight: 1.5 }}>
                        {displaySentence(segments[currentSegment - 1]?.text)}
                      </p>
                    )}
                    {/* Aktueller Satz — Champagne beim Vorlesen */}
                    <p
                      className="font-display text-[26px] transition-colors duration-500"
                      style={{ color: isPlaying ? CHAMPAGNE : CREAM, lineHeight: 1.45 }}
                      data-testid="lesung-current-sentence"
                    >
                      {displaySentence(segments[currentSegment]?.text)}
                    </p>
                  </div>
                )}
              </div>

              {/* Audio-Leiste */}
              <div className="flex items-center gap-4 mt-5" data-testid="lesung-audio-bar">
                {/* Play/Pause — Gold-Outline-Kreis */}
                <button
                  onClick={isPlaying ? pauseLesson : (progress > 0 && progress < 100 ? resumeLesson : startLesson)}
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                  style={{ border: `1.5px solid ${GOLD}`, color: GOLD }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(216,146,50,0.12)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  data-testid={isPlaying ? 'pause-lesson-btn' : 'resume-lesson-btn'}
                >
                  {isPlaying ? <Pause className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> : <Play style={{ width: 18, height: 18, marginLeft: 2 }} />}
                </button>

                {/* Gold-Fortschrittslinie mit Segment-Markern */}
                <div className="relative flex-1 flex items-center" style={{ height: 20 }}>
                  <div className="absolute inset-x-0" style={{ height: 2, backgroundColor: 'rgba(216,146,50,0.25)' }} />
                  <div className="absolute left-0 transition-all duration-500" style={{ height: 2, width: `${progress}%`, backgroundColor: GOLD }} />
                  {segments.map((_, idx) => (
                    <span
                      key={idx}
                      className="absolute rounded-full transition-colors duration-300"
                      style={{
                        width: 6, height: 6,
                        left: `calc(${segments.length > 1 ? (idx / (segments.length - 1)) * 100 : 0}% - 3px)`,
                        backgroundColor: idx <= currentSegment ? GOLD : 'rgba(216,146,50,0.3)',
                      }}
                    />
                  ))}
                </div>

                <span className="font-mono text-[9.5px] uppercase font-semibold whitespace-nowrap flex-shrink-0 text-sand-600" style={{ letterSpacing: '0.2em' }}>
                  Segment {Math.min(currentSegment + 1, segments.length)} von {segments.length}
                </span>

                <button
                  onClick={resetLesson}
                  className="p-2 text-sand-500 hover:text-terra transition-colors flex-shrink-0"
                  title="Neustart"
                  data-testid="reset-lesson-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>

              {/* Abschluss-Aktionen */}
              {showInteraction && (
                <div className="mt-6 py-5 pl-6 pr-5 rounded-r-lg" style={{ backgroundColor: '#EDE6DC', borderLeft: `2px solid ${GOLD}` }}>
                  <p className="font-display italic text-[17px] mb-4" style={{ color: TERRA }}>
                    Gut zugehört. Wie geht es weiter?
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={nextLesson}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[12px] uppercase tracking-[0.12em] transition-colors"
                      style={{ border: `1.5px solid ${TERRA}`, color: TERRA }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = TERRA; e.currentTarget.style.color = CREAM; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TERRA; }}
                      data-testid="lesson-understood-btn"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {currentLesson < lessons.length - 1 ? 'Nächste Lesung' : 'Abschließen'}
                    </button>
                    <button
                      onClick={() => { resetLesson(); setTimeout(startLesson, 100); }}
                      className="flex items-center gap-2 px-4 py-2.5 text-sand-600 hover:text-terra transition-colors text-sm"
                      data-testid="lesson-repeat-btn"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Nochmal anhören
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Rechts: Mitschrift — Magazin-Spalte */}
            <div className="lg:col-span-2">
              <p className="font-mono text-[10px] uppercase font-semibold pb-3 border-b" style={{ letterSpacing: '0.24em', color: GOLD_INK, borderColor: '#E0D6CA' }}>
                Mitschrift
              </p>
              <div className="max-h-[420px] overflow-y-auto pt-4 pr-1" data-testid="lesung-transcript">
                {segments.map((seg, idx) => (
                  <p
                    key={idx}
                    className="text-[15px] leading-relaxed mb-3.5 transition-all duration-300"
                    style={{
                      color: idx === currentSegment ? '#3D3A36' : idx < currentSegment ? '#8A8175' : '#B4AA9C',
                      paddingLeft: idx === currentSegment ? 14 : 0,
                      borderLeft: idx === currentSegment ? `2px solid ${GOLD}` : '2px solid transparent',
                    }}
                  >
                    {seg.text}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lesungs-Navigation */}
      {lessons.length > 1 && (
        <div className="px-6 py-4 border-t border-cream-300">
          <div className="flex items-center justify-between">
            <button
              onClick={prevLesson}
              disabled={currentLesson === 0}
              className="flex items-center gap-2 px-4 py-2 text-sand-600 hover:text-terra disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm"
              data-testid="prev-lesson-btn"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              Vorherige
            </button>

            <div className="flex gap-2">
              {lessons.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => { resetLesson(); setCurrentLesson(idx); }}
                  className="w-2.5 h-2.5 rounded-full transition-all duration-200"
                  style={{ backgroundColor: idx === currentLesson ? TERRA : '#E0D6CA' }}
                  data-testid={`lesson-dot-${idx}`}
                />
              ))}
            </div>

            <button
              onClick={nextLesson}
              disabled={currentLesson === lessons.length - 1}
              className="flex items-center gap-2 px-4 py-2 text-sand-600 hover:text-terra disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm"
              data-testid="next-lesson-btn"
            >
              Nächste
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Hidden audio element */}
      <audio ref={audioRef} />
    </div>
  );
};

export default DickiesVideoLesson;
